import { ICardElementWithChildren, IWFState } from "backend/model/model";

export const WF_ROLE_FINAL_APPROVE = "__WF_DATA_FINAL_APPROVE";
export const WF_ROLE_DATA_INPUT = "__WF_DATA_INPUT";
export const WF_ROLE_DATA_CHECKER = "__WF_DATA_CHECKER";
export const WF_ROLE_DATA_APPROVE = "__WF_DATA_APPROVE";
export const WF_ROLE_DATA_CEO = "__WF_DATA_CEO";

const KNOWN_WORKFLOW_ROLES = new Set([
  WF_ROLE_DATA_INPUT,
  WF_ROLE_DATA_CHECKER,
  WF_ROLE_DATA_APPROVE,
  WF_ROLE_FINAL_APPROVE,
  WF_ROLE_DATA_CEO,
]);

/** True when the session has a recognized workflow role (including CEO). */
export function hasAppWorkflowRole(userWorkflowRole?: string | null): boolean {
  return Boolean(
    userWorkflowRole && KNOWN_WORKFLOW_ROLES.has(userWorkflowRole)
  );
}

/** True when the user should see My Tasks period/division filters. */
export function isMyTasksFinalApprovalUser(
  isAdmin: boolean,
  userWorkflowRole?: string | null
): boolean {
  return userWorkflowRole === WF_ROLE_FINAL_APPROVE || isAdmin;
}

/** Fetch all tasks (no server pagination) when client-side role filtering is required. */
export function shouldUseMyTasksRoleFiltering(
  isAdmin: boolean,
  userWorkflowRole?: string | null
): boolean {
  if (isAdmin) return true;
  return (
    userWorkflowRole === WF_ROLE_DATA_INPUT ||
    userWorkflowRole === WF_ROLE_DATA_CHECKER ||
    userWorkflowRole === WF_ROLE_DATA_APPROVE ||
    userWorkflowRole === WF_ROLE_FINAL_APPROVE
  );
}

/** Server `include` param for getMyTasks. */
export function getMyTasksFetchIncludeType(
  needsRoleFiltering: boolean,
  showOnlyCurrentTasks: boolean
): "all" | "current" | "coming" {
  if (needsRoleFiltering) return "all";
  return showOnlyCurrentTasks ? "current" : "coming";
}

export function isFinalApprovalWorkflowTask(
  workflowState?: IWFState | null
): boolean {
  if (!workflowState || workflowState.wfShortType !== "value") return false;
  const shortState = workflowState.shortState || "";
  const state = (workflowState.state || "").toLowerCase();
  return (
    shortState === "finalapproval" ||
    state.includes("final approval") ||
    state.includes("finalapproval")
  );
}

export function isCheckerWorkflowTask(workflowState?: IWFState | null): boolean {
  if (!workflowState || workflowState.wfShortType !== "value") return false;
  const shortState = (workflowState.shortState || "").toLowerCase();
  const state = (workflowState.state || "").toLowerCase();
  return (
    shortState === "check" ||
    shortState === "checker" ||
    state.includes("waiting for check") ||
    (state.includes("check") && !state.includes("rejected"))
  );
}

/**
 * Tasks visible to admin / final approver before period/division filters.
 * Shows all final-approval value workflows across periods.
 */
export function filterTasksForFinalApprovalViewer(
  tasks: ICardElementWithChildren[]
): ICardElementWithChildren[] {
  return tasks.filter((task) =>
    isFinalApprovalWorkflowTask(task.workflowState)
  );
}

export function filterMyTasksByWorkflowRole(
  tasks: ICardElementWithChildren[],
  options: {
    isAdmin: boolean;
    userWorkflowRole?: string | null;
    userFullname?: string;
    isTaskRelatedToUser?: (task: ICardElementWithChildren) => boolean;
  }
): ICardElementWithChildren[] {
  const { isAdmin, userWorkflowRole, userFullname } = options;

  if (isAdmin || userWorkflowRole === WF_ROLE_FINAL_APPROVE) {
    return filterTasksForFinalApprovalViewer(tasks);
  }

  if (!userWorkflowRole) return tasks;

  return tasks.filter((task) => {
    const workflowState = task.workflowState;
    if (!workflowState) return false;

    const shortState = workflowState.shortState || "";
    const isKPI = workflowState.measureType === "KPI";
    const isValueType = workflowState.wfShortType === "value";
    const isResponsible = workflowState.responsible === userFullname;

    if (userWorkflowRole === WF_ROLE_DATA_INPUT) {
      if (isValueType) {
        return (
          shortState === "dataentry" ||
          shortState === "approvalrejected" ||
          shortState === "checkrejected" ||
          shortState === "update"
        );
      }
      return isResponsible;
    }

    if (userWorkflowRole === WF_ROLE_DATA_CHECKER && isValueType) {
      // Same maker/start-workflow KPIs as input users, plus check-stage KPIs.
      return (
        shortState === "dataentry" ||
        shortState === "approvalrejected" ||
        shortState === "checkrejected" ||
        shortState === "update" ||
        isCheckerWorkflowTask(workflowState)
      );
    }

    if (userWorkflowRole === WF_ROLE_DATA_APPROVE && isValueType) {
      // Match Task Workflow: division approvers see all approval-stage value KPIs
      // returned by My Tasks API (access already enforced server-side).
      return shortState === "approval";
    }

    if (!isResponsible) return false;

    if (isResponsible && isKPI && isValueType) {
      return (
        shortState === "dataentry" ||
        shortState === "approvalrejected" ||
        shortState === "checkrejected"
      );
    }

    return true;
  });
}
